/**
 * Itinerario del trineo: 13 tramos y 58 paradas (56 entregas más la salida y la
 * vuelta al Polo Norte), ordenadas por huso horario descendente (UTC+13 → UTC−10).
 *
 * **Por qué el orden lo manda el huso y no la geografía.** La Nochebuena
 * recorre el planeta de este a oeste una hora cada vez; ese barrido es lo que
 * hace que la estimación para el visitante (`schedule.ts`) y la posición del
 * trineo cuenten la misma historia. La consecuencia visible es que África
 * (El Cairo y Johannesburgo, UTC+2) va **antes** que Europa occidental (UTC+1),
 * al revés que en un listado geográfico al uso.
 *
 * **Por qué no está Kiritimati.** La Isla de Navidad (UTC+14) y Samoa (+13)
 * caen al otro lado de la línea de cambio de fecha: en el mapa equirectangular
 * su longitud (−157 y −172) las manda al lado contrario de la pantalla y la
 * ruta pegaría un salto de un extremo a otro. La primera parada dibujable es
 * Auckland (+13, longitud 174,8), y el dato de que la Navidad empieza en
 * Kiritimati se cuenta en las preguntas frecuentes.
 *
 * Los desfases son los de **diciembre**, con el horario de verano austral ya
 * aplicado (Auckland +13, Sídney +11).
 *
 * `countries` reparte los 195 países del mundo entre las paradas, de modo que
 * el contador de "países visitados" llega exactamente a 195 al terminar el
 * recorrido (invariante fijado en los tests).
 */

import type { RouteLegKey, RouteStop } from './types'

/** Los 13 tramos, en orden de recorrido. */
export const ROUTE_LEGS: readonly RouteLegKey[] = [
  'north-pole',
  'pacific',
  'oceania',
  'east-asia',
  'southeast-asia',
  'central-asia',
  'middle-east',
  'eastern-europe',
  'africa',
  'western-europe',
  'south-america',
  'north-america',
  'north-pole-return',
] as const

/**
 * Hora local a la que pasa el trineo por cada parada: el punto medio de la
 * franja «entre las 21:00 y las 00:00» que se le promete al visitante.
 */
export const DELIVERY_LOCAL_HOUR = 22

/** Minutos de vuelo desde el Polo Norte hasta la primera entrega. */
export const DEPARTURE_LEAD_MINUTES = 60

/** Minutos de vuelo de vuelta a casa tras la última entrega. */
export const RETURN_FLIGHT_MINUTES = 120

/** Países del mundo entre los que se reparte el contador. */
export const WORLD_COUNTRIES = 195

/**
 * Coordenadas del Polo Norte tal y como se dibujan: la latitud real (90) cae
 * fuera del recorte del mapa y, sobre todo, en una proyección equirectangular
 * el polo es toda la arista superior. 87° lo deja centrado y visible.
 */
export const NORTH_POLE_LAT = 87
export const NORTH_POLE_LON = 0

const CITY_STOPS: readonly RouteStop[] = [
  // Pacífico
  { id: 'auckland', leg: 'pacific', utcOffset: 13, lon: 174.8, lat: -36.9, countries: 1 },
  { id: 'suva', leg: 'pacific', utcOffset: 12, lon: 178.4, lat: -18.1, countries: 5 },
  // Oceanía
  { id: 'sydney', leg: 'oceania', utcOffset: 11, lon: 151.2, lat: -33.9, countries: 1 },
  { id: 'melbourne', leg: 'oceania', utcOffset: 11, lon: 144.9, lat: -37.8, countries: 0 },
  { id: 'brisbane', leg: 'oceania', utcOffset: 10, lon: 153.0, lat: -27.5, countries: 2 },
  { id: 'port-moresby', leg: 'oceania', utcOffset: 10, lon: 147.2, lat: -9.4, countries: 3 },
  { id: 'darwin', leg: 'oceania', utcOffset: 9.5, lon: 130.8, lat: -12.5, countries: 0 },
  // Asia oriental
  { id: 'tokyo', leg: 'east-asia', utcOffset: 9, lon: 139.7, lat: 35.7, countries: 1 },
  { id: 'seoul', leg: 'east-asia', utcOffset: 9, lon: 127.0, lat: 37.6, countries: 2 },
  { id: 'shanghai', leg: 'east-asia', utcOffset: 8, lon: 121.5, lat: 31.2, countries: 1 },
  { id: 'beijing', leg: 'east-asia', utcOffset: 8, lon: 116.4, lat: 39.9, countries: 3 },
  { id: 'hong-kong', leg: 'east-asia', utcOffset: 8, lon: 114.2, lat: 22.3, countries: 1 },
  // Sudeste asiático
  { id: 'manila', leg: 'southeast-asia', utcOffset: 8, lon: 121.0, lat: 14.6, countries: 1 },
  { id: 'singapore', leg: 'southeast-asia', utcOffset: 8, lon: 103.8, lat: 1.4, countries: 3 },
  { id: 'jakarta', leg: 'southeast-asia', utcOffset: 7, lon: 106.8, lat: -6.2, countries: 2 },
  { id: 'bangkok', leg: 'southeast-asia', utcOffset: 7, lon: 100.5, lat: 13.8, countries: 5 },
  // Asia central y del sur
  { id: 'dhaka', leg: 'central-asia', utcOffset: 6, lon: 90.4, lat: 23.8, countries: 3 },
  { id: 'kathmandu', leg: 'central-asia', utcOffset: 5.75, lon: 85.3, lat: 27.7, countries: 2 },
  { id: 'new-delhi', leg: 'central-asia', utcOffset: 5.5, lon: 77.2, lat: 28.6, countries: 2 },
  { id: 'karachi', leg: 'central-asia', utcOffset: 5, lon: 67.0, lat: 24.9, countries: 5 },
  { id: 'kabul', leg: 'central-asia', utcOffset: 4.5, lon: 69.2, lat: 34.5, countries: 2 },
  // Oriente Medio
  { id: 'dubai', leg: 'middle-east', utcOffset: 4, lon: 55.3, lat: 25.2, countries: 6 },
  { id: 'tehran', leg: 'middle-east', utcOffset: 3.5, lon: 51.4, lat: 35.7, countries: 2 },
  { id: 'riyadh', leg: 'middle-east', utcOffset: 3, lon: 46.7, lat: 24.7, countries: 8 },
  // Europa oriental
  { id: 'istanbul', leg: 'eastern-europe', utcOffset: 3, lon: 29.0, lat: 41.0, countries: 2 },
  { id: 'moscow', leg: 'eastern-europe', utcOffset: 3, lon: 37.6, lat: 55.8, countries: 6 },
  { id: 'athens', leg: 'eastern-europe', utcOffset: 2, lon: 23.7, lat: 38.0, countries: 6 },
  { id: 'warsaw', leg: 'eastern-europe', utcOffset: 2, lon: 21.0, lat: 52.2, countries: 10 },
  // África
  { id: 'cairo', leg: 'africa', utcOffset: 2, lon: 31.2, lat: 30.0, countries: 6 },
  { id: 'johannesburg', leg: 'africa', utcOffset: 2, lon: 28.0, lat: -26.2, countries: 12 },
  { id: 'lagos', leg: 'africa', utcOffset: 1, lon: 3.4, lat: 6.5, countries: 36 },
  // Europa occidental
  { id: 'berlin', leg: 'western-europe', utcOffset: 1, lon: 13.4, lat: 52.5, countries: 6 },
  { id: 'rome', leg: 'western-europe', utcOffset: 1, lon: 12.5, lat: 41.9, countries: 5 },
  { id: 'paris', leg: 'western-europe', utcOffset: 1, lon: 2.3, lat: 48.9, countries: 5 },
  { id: 'madrid', leg: 'western-europe', utcOffset: 1, lon: -3.7, lat: 40.4, countries: 2 },
  { id: 'london', leg: 'western-europe', utcOffset: 0, lon: -0.1, lat: 51.5, countries: 2 },
  { id: 'lisbon', leg: 'western-europe', utcOffset: 0, lon: -9.1, lat: 38.7, countries: 1 },
  { id: 'reykjavik', leg: 'western-europe', utcOffset: 0, lon: -21.9, lat: 64.1, countries: 1 },
  // Sudamérica
  { id: 'rio-de-janeiro', leg: 'south-america', utcOffset: -3, lon: -43.2, lat: -22.9, countries: 1 },
  { id: 'sao-paulo', leg: 'south-america', utcOffset: -3, lon: -46.6, lat: -23.5, countries: 0 },
  { id: 'buenos-aires', leg: 'south-america', utcOffset: -3, lon: -58.4, lat: -34.6, countries: 3 },
  { id: 'santiago', leg: 'south-america', utcOffset: -3, lon: -70.6, lat: -33.4, countries: 1 },
  { id: 'la-paz', leg: 'south-america', utcOffset: -4, lon: -68.1, lat: -16.5, countries: 2 },
  { id: 'caracas', leg: 'south-america', utcOffset: -4, lon: -66.9, lat: 10.5, countries: 3 },
  { id: 'bogota', leg: 'south-america', utcOffset: -5, lon: -74.1, lat: 4.7, countries: 2 },
  { id: 'lima', leg: 'south-america', utcOffset: -5, lon: -77.0, lat: -12.0, countries: 1 },
  // Norteamérica
  { id: 'new-york', leg: 'north-america', utcOffset: -5, lon: -74.0, lat: 40.7, countries: 1 },
  { id: 'toronto', leg: 'north-america', utcOffset: -5, lon: -79.4, lat: 43.7, countries: 1 },
  { id: 'havana', leg: 'north-america', utcOffset: -5, lon: -82.4, lat: 23.1, countries: 8 },
  { id: 'mexico-city', leg: 'north-america', utcOffset: -6, lon: -99.1, lat: 19.4, countries: 1 },
  { id: 'chicago', leg: 'north-america', utcOffset: -6, lon: -87.6, lat: 41.9, countries: 6 },
  { id: 'denver', leg: 'north-america', utcOffset: -7, lon: -105.0, lat: 39.7, countries: 0 },
  { id: 'los-angeles', leg: 'north-america', utcOffset: -8, lon: -118.2, lat: 34.1, countries: 0 },
  { id: 'vancouver', leg: 'north-america', utcOffset: -8, lon: -123.1, lat: 49.3, countries: 0 },
  { id: 'anchorage', leg: 'north-america', utcOffset: -9, lon: -149.9, lat: 61.2, countries: 0 },
  { id: 'honolulu', leg: 'north-america', utcOffset: -10, lon: -157.9, lat: 21.3, countries: 4 },
] as const

/** Parada de salida (el trineo despega de aquí). */
export const DEPARTURE_STOP: RouteStop = {
  id: 'north-pole',
  leg: 'north-pole',
  utcOffset: 13,
  lon: NORTH_POLE_LON,
  lat: NORTH_POLE_LAT,
  countries: 0,
}

/** Parada de llegada (vuelve a casa). Mismo sitio, distinto momento. */
export const RETURN_STOP: RouteStop = {
  id: 'north-pole-return',
  leg: 'north-pole-return',
  utcOffset: -10,
  lon: NORTH_POLE_LON,
  lat: NORTH_POLE_LAT,
  countries: 0,
}

/** Itinerario completo: salida → entregas → vuelta a casa. */
export const ROUTE: readonly RouteStop[] = [DEPARTURE_STOP, ...CITY_STOPS, RETURN_STOP]

/** Solo las paradas con entrega (sin los dos extremos del Polo Norte). */
export const DELIVERY_STOPS: readonly RouteStop[] = CITY_STOPS

/**
 * Países acumulados tras cada parada del itinerario (índice → total). Se
 * precalcula porque el contador se lee en cada tick del reloj.
 */
export const CUMULATIVE_COUNTRIES: readonly number[] = ROUTE.reduce<number[]>(
  (acc, stop, index) => {
    acc.push((index === 0 ? 0 : acc[index - 1]) + stop.countries)
    return acc
  },
  []
)

const STOPS_BY_ID = new Map(ROUTE.map((stop) => [stop.id, stop]))

export function getStopById(id: string): RouteStop | undefined {
  return STOPS_BY_ID.get(id)
}

/** Índice de la parada en el itinerario, o −1. */
export function getStopIndex(id: string): number {
  return ROUTE.findIndex((stop) => stop.id === id)
}
